import BigNumber from 'bignumber.js';
import moment from 'moment-timezone';
import { PRICING_MODELS } from 'sonata-static-storage/lib/samples/pricingModels.samples';
import {
  type BidModelInterface,
  BidModelsMediaTypeEnum,
  PricingModelEnum
} from 'sonata-static-storage/lib/schemas/campaign/bidModel.schema';
import { CampaignFamilyEnum } from 'sonata-static-storage/lib/schemas/campaign/campaign.schema';
import { GoalTimeFrameEnum, GoalTypeEnum } from 'sonata-static-storage/lib/schemas/campaign/goal.schema';
import { AdExchangeCodesEnum } from 'sonata-static-storage/lib/schemas/campaign/targeting/adExchange.schema';
import { MediaTypeValidator } from 'sonata-static-storage/lib/schemas/mediaType/MediaType.validator';
import {
  AgencyFeeModelEnum,
  dCpmModel,
  PlatformFeeModelEnum
} from 'sonata-static-storage/lib/schemas/user/user.schema';
import { z } from 'zod';

import type { IntlShape } from 'react-intl';
import type { MultiFlightEntityType } from 'sonata-static-storage/lib/common/common.types';
import type { InsertionOrder } from 'sonata-static-storage/lib/entities';
import type { FormSection, FormSubsection } from '@/types/formSections.types';

type AdditionalValidatorProps = {
  allZonesRetargeting?: boolean;
  beginDate?: string | Date | null;
  bidModels?: BidModelInterface[];
  entityType?: MultiFlightEntityType;
  endDate?: string | Date | null;
  excludeAdExchanges?: boolean;
  excludeAds?: boolean;
  isCompleted?: boolean;
  isDistributionActive?: boolean;
  minimumBidPrices?: Record<keyof typeof BidModelsMediaTypeEnum, number>;
  type?: InsertionOrder['type'];
};

/**
 * Map of schema field paths to their corresponding section and subsection.
 *
 * Structure:
 * - Top-level keys: Schema field names (e.g., 'targeting', 'flights')
 * - section: Maps to FormSection (mainSettings, locations, contextTargeting, creatives)
 * - subsectionMap: Maps nested paths to FormSubsection
 *   - Empty string ('') is the default fallback for direct field errors
 *   - Specific keys (e.g., 'adExchanges', 'contextualTargeting') map nested errors
 *
 * Usage & Extension:
 * When adding new schema fields or nesting levels:
 * 1. Add the field to this map with its corresponding section
 * 2. If the field has nested errors, add subsectionMap entries
 * 3. Use '' as fallback subsection for fields without nested validation
 */
type SectionPathMap = {
  [key: string]: {
    section: FormSection;
    subsectionMap?: Record<string, FormSubsection>;
  };
};

const SECTION_PATH_MAP: SectionPathMap = {
  // Simple fields mapping to sections with default subsections
  ads: {
    section: 'creatives',
    subsectionMap: {
      default: 'adCreatives' // Default fallback for direct ads errors
    }
  },
  beginDate: {
    section: 'mainSettings',
    subsectionMap: {
      default: 'schedulingDistribution'
    }
  },
  countries: {
    section: 'locations',
    subsectionMap: {
      default: 'locationQualityIndexes'
    }
  },
  dco: {
    section: 'creatives',
    subsectionMap: {
      default: 'contextualSettings'
    }
  },
  endDate: {
    section: 'mainSettings',
    subsectionMap: {
      default: 'schedulingDistribution'
    }
  },
  flights: {
    section: 'mainSettings',
    subsectionMap: {
      default: 'flightSettings'
    }
  },
  goals: {
    section: 'mainSettings',
    subsectionMap: {
      default: 'budgetAndGoals'
    }
  },
  ioNumber: {
    section: 'mainSettings',
    subsectionMap: {
      default: 'ioSettings'
    }
  },
  name: {
    section: 'mainSettings',
    subsectionMap: {
      default: 'ioSettings'
    }
  },
  pricingModelConfig: {
    section: 'mainSettings',
    subsectionMap: {
      default: 'pricingModel'
    }
  },
  scheduleParting: {
    section: 'mainSettings',
    subsectionMap: {
      default: 'schedulingDistribution'
    }
  },
  targeting: {
    section: 'contextTargeting',
    subsectionMap: {
      adExchanges: 'supplySource',
      appAndSiteMarketplace: 'appMarketplace',
      contextualTargeting: 'contextualBidding',
      retargeting: 'retargeting',
      temperatureAndWeather: 'temperatureAndWeather'
    }
  },
  timezone: {
    section: 'mainSettings',
    subsectionMap: {
      default: 'schedulingDistribution'
    }
  },
  type: {
    section: 'mainSettings',
    subsectionMap: {
      default: 'mediaChannel'
    }
  },
  userFrequencyCapping: {
    section: 'mainSettings',
    subsectionMap: {
      default: 'budgetAndGoals'
    }
  }
};

export type SectionErrorMap = {
  [section: string]: {
    total: number;
    subsections: {
      [subsection: string]: number;
    };
  };
};

/**
 * Maps Zod validation errors to aggregated section and subsection structure.
 *
 * Purpose:
 * - Aggregates validation errors by section and subsection for side menu badges
 * - Enables dynamic error count display in UI without individual notifications
 * - Supports both InsertionOrderSchema and LineItemSchema validation
 *
 * Implementation Details:
 * 1. Parses Zod error paths (e.g., ['targeting', 'adExchanges', 'distribution'])
 * 2. Maps first path element to section using SECTION_PATH_MAP
 * 3. Maps subsequent path elements to subsection using nested maps
 * 4. Falls back to '' (empty string) subsection map for simple field errors
 * 5. Accumulates error counts per section and subsection
 *
 * Return Structure:
 * {
 *   "mainSettings": {
 *     "total": 5,
 *     "subsections": {
 *       "ioSettings": 2,
 *       "pricingModel": 3
 *     }
 *   },
 *   "contextTargeting": {
 *     "total": 2,
 *     "subsections": {
 *       "supplySource": 2
 *     }
 *   }
 * }
 *
 * Important Notes:
 * - Inline validation (red highlights, field-level error messages) remains unchanged
 * - This function only aggregates for section/subsection badges
 * - The side menu displays section badges with total counts
 * - Subsections display their own error count badges dynamically
 * - Single generic notification replaces multiple validation notifications
 * - Extensible: add new fields to SECTION_PATH_MAP to support schema extensions
 *
 * @param error ZodError containing validation issues from schema validation
 * @returns SectionErrorMap with aggregated error counts per section and subsection
 */
export function mapZodErrorsToSectionErrorMap(error: z.ZodError): SectionErrorMap {
  const errorMap: SectionErrorMap = {};

  error.issues.forEach(issue => {
    if (!issue.path || issue.path.length === 0) {
      return;
    }

    const firstPathElement = String(issue.path[0]);
    const secondPathElement = issue.path[1] ? String(issue.path[1]) : '';

    const pathConfig = SECTION_PATH_MAP[firstPathElement];

    if (!pathConfig) {
      return;
    }

    const section = pathConfig.section;
    const subsectionMap = pathConfig.subsectionMap || {};
    const subsection = subsectionMap[secondPathElement] || subsectionMap.default || firstPathElement;

    // Initialize section if it doesn't exist
    if (!errorMap[section]) {
      errorMap[section] = {
        subsections: {},
        total: 0
      };
    }

    // Initialize subsection if it doesn't exist
    if (!errorMap[section].subsections[subsection]) {
      errorMap[section].subsections[subsection] = 0;
    }

    // Increment counters
    errorMap[section].total += 1;
    errorMap[section].subsections[subsection] += 1;
  });

  return errorMap;
}

function getCountriesSchema(formatMessage: IntlShape['formatMessage']) {
  return z.array(z.string()).min(1, {
    message: formatMessage({
      defaultMessage: 'Select at least one country',
      id: 'ioSettings.errors.countries'
    })
  });
}

function getDcoSchema(formatMessage: IntlShape['formatMessage']) {
  return z.object({
    enabled: z.boolean(),
    scenarios: z.array(
      z.object({
        ads: z.array(z.string()).min(1, {
          message: formatMessage({
            defaultMessage: 'You must associate at least ad creative with each scenario.',
            id: 'ioSettings.errors.dco.scenarios.ads'
          })
        }),
        defaultScenario: z.boolean(),
        priority: z.number(),
        removed: z.boolean().optional(),
        scenario: z.string()
      })
    )
  });
}

function getBeginDateSchema(formatMessage: IntlShape['formatMessage']) {
  return z.preprocess(
    val => (val instanceof Date ? val.toISOString() : val),
    z.nullable(z.string()).superRefine((beginDate: string | null, ctx) => {
      const ok = beginDate ? !isNaN(Date.parse(beginDate)) : null;

      if (!ok) {
        ctx.addIssue({
          code: 'custom',
          message: formatMessage({
            defaultMessage: 'Begin date is required',
            id: 'ioSettings.errors.beginDate'
          })
        });
      }
    })
  );
}

function getEndDateSchema(formatMessage: IntlShape['formatMessage']) {
  return z.preprocess(
    val => (val instanceof Date ? val.toISOString() : val),
    z.nullable(z.string()).superRefine((endDate: string | null, ctx) => {
      const ok = endDate ? !isNaN(Date.parse(endDate)) : null;

      if (!ok) {
        ctx.addIssue({
          code: 'custom',
          message: formatMessage({
            defaultMessage: 'End date is required',
            id: 'ioSettings.errors.endDate'
          })
        });
      }
    })
  );
}

function getFlightsSchema(formatMessage: IntlShape['formatMessage'], additionalProps: AdditionalValidatorProps) {
  const flightSchema = z.object({
    beginDate: z
      .string({
        message: formatMessage({
          defaultMessage: 'The flight begin date is required',
          id: 'ioSettings.errors.flights.beginDate'
        })
      })
      .superRefine((beginDate: string, ctx) => {
        // Flight dates are stored as offset-less UTC midnights; compare against the user's calendar day
        const beginDateFormatted = moment.utc(beginDate);
        const today = moment.utc(moment().format('YYYY-MM-DD'));
        if (beginDateFormatted.isBefore(today) && !additionalProps.isCompleted) {
          ctx.addIssue({
            code: 'custom',
            message: formatMessage({
              defaultMessage: 'The flight start date must be today or a later date',
              id: 'ioSettings.errors.flights.beginDateBeforeToday'
            })
          });
        }
      }),
    endDate: z.string({
      message: formatMessage({
        defaultMessage: 'The flight end date is required',
        id: 'ioSettings.errors.flights.endDate'
      })
    }),
    goal: z.object({
      target: z
        .number({
          invalid_type_error: formatMessage({
            defaultMessage: 'The flight budget value must be a number',
            id: 'ioSettings.errors.flights.budgetInvalid'
          }),
          required_error: formatMessage({
            defaultMessage: 'The flight budget value is required',
            id: 'ioSettings.errors.flights.budget'
          })
        })
        .gt(0, {
          message: formatMessage({
            defaultMessage: 'Budget value must be greater than zero',
            id: 'ioSettings.errors.flights.budgetGreaterThanZero'
          })
        })
    }),
    name: z.string().min(1, {
      message: formatMessage({
        defaultMessage: 'The flight name field must be filled',
        id: 'ioSettings.errors.flights.name'
      })
    })
  });

  return z.array(z.unknown()).superRefine((flights, ctx) => {
    if (!Array.isArray(flights) || flights.length < 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_small,
        inclusive: true,
        message: formatMessage({
          defaultMessage: 'You must configure at least one valid flight for the insertion order to proceed.',
          id: 'ioSettings.errors.flights'
        }),
        minimum: 1,
        type: 'array'
      });
      return;
    }

    flights.forEach(f => {
      const res = flightSchema.safeParse(f);
      if (!res.success) {
        res.error.issues.forEach(issue => {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: issue.message
          });
        });
      }
    });
  });
}

function getIoNumberSchema(formatMessage: IntlShape['formatMessage']) {
  const errorMessage = formatMessage({
    defaultMessage: 'The IO number is required',
    id: 'ioSettings.errors.ioNumber'
  });

  return z
    .string({
      invalid_type_error: errorMessage,
      required_error: errorMessage
    })
    .trim()
    .min(1, { message: errorMessage });
}

function getGoalsSchema(formatMessage: IntlShape['formatMessage']) {
  return z
    .array(
      z.object({
        target: z.nullable(
          z.number({
            message: formatMessage({
              defaultMessage: 'Goals must be numbers',
              id: 'ioSettings.errors.goals.budget'
            })
          })
        ),
        timeFrame: z.nativeEnum(GoalTimeFrameEnum, {
          message: formatMessage({
            defaultMessage: 'Wrong goal timeFrame',
            id: 'ioSettings.errors.goals.timeFrame'
          })
        }),
        type: z.nullable(
          z.nativeEnum(GoalTypeEnum, {
            message: formatMessage({
              defaultMessage: 'Wrong goal type',
              id: 'ioSettings.errors.goals.type'
            })
          })
        )
      })
    )
    .superRefine((arr, ctx) => {
      const hasBudget = arr?.some(g => g.type === GoalTypeEnum.budget && typeof g.target === 'number' && g.target > 0);
      if (!hasBudget) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: formatMessage({
            defaultMessage: 'A budget goal must be set',
            id: 'ioSettings.errors.goals.budgetGoal'
          })
        });
      }

      const hasPositiveTarget = arr?.some(g => typeof g.target === 'number' && g.target > 0);

      if (!hasPositiveTarget) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: formatMessage({
            defaultMessage: 'At least one goal must be set',
            id: 'ioSettings.errors.goals.atLeastOneGoal'
          })
        });
      }
    });
}

function getNameSchema(formatMessage: IntlShape['formatMessage']) {
  const errorMessage = formatMessage({
    defaultMessage: 'The name field must be filled',
    id: 'ioSettings.errors.name'
  });

  return z
    .string({
      invalid_type_error: errorMessage,
      required_error: errorMessage
    })
    .trim()
    .min(1, {
      message: errorMessage
    });
}

function getBidModelsSchema(formatMessage: IntlShape['formatMessage'], additionalProps: AdditionalValidatorProps) {
  return z
    .array(
      z
        .object({
          bid: z.object({
            dynamicBidPrice: z.boolean(),
            dynamicBidPriceManualSet: z.boolean().default(false),
            price: z.number(),
            pricingModel: z.string()
          }),
          distribution: z.number().optional(),
          mediaType: MediaTypeValidator
        })
        .superRefine((data: BidModelInterface, ctx) => {
          const minBidPrice =
            additionalProps?.minimumBidPrices?.[data.mediaType as keyof typeof BidModelsMediaTypeEnum] || 0;
          const pricingModel = data.bid.pricingModel;
          const associatedPricingModel = PRICING_MODELS.find(({ model }) => model === pricingModel);
          const bidPriceSet = associatedPricingModel?.perMil
            ? new BigNumber(data.bid.price).multipliedBy(1000)
            : new BigNumber(data.bid.price);
          if (bidPriceSet.isLessThan(minBidPrice)) {
            ctx.addIssue({
              code: 'custom',
              message: formatMessage({
                defaultMessage: 'The bid price cannot be lower than the minimum.',
                id: 'ioSettings.errors.pricingModelConfig.minBidPrice'
              }),
              path: ['bid', 'price']
            });
          }
        })
    )
    .min(1)
    .max(2);
}

function getPricingModelConfigSchema(
  formatMessage: IntlShape['formatMessage'],
  additionalProps: AdditionalValidatorProps
) {
  return z.object({
    agencyFeeModel: z.nativeEnum(AgencyFeeModelEnum, {
      message: formatMessage({
        defaultMessage: 'An Agency Fee Model must be chosen.',
        id: 'ioSettings.errors.pricingModelConfig.agencyFeeModel'
      })
    }),
    bidModels: getBidModelsSchema(formatMessage, additionalProps),
    cpalEvent: z.string({
      message: formatMessage({
        defaultMessage: 'Select one option.',
        id: 'ioSettings.errors.pricingModelConfig.eventOption'
      })
    }),
    cpvEvent: z.string({
      message: formatMessage({
        defaultMessage: 'Select one option.',
        id: 'ioSettings.errors.pricingModelConfig.eventOption'
      })
    }),
    cpxOnlyMedia: z.boolean(),
    dCPMModel: z.nativeEnum(dCpmModel, {
      message: formatMessage({
        defaultMessage: 'A D-CPM Model must be chosen.',
        id: 'ioSettings.errors.pricingModelConfig.dCPMModel'
      })
    }),
    expectedATR: z.number().min(0, {
      message: formatMessage({
        defaultMessage: 'Expected ATR must be a valid value.',
        id: 'ioSettings.errors.pricingModelConfig.expectedATR'
      })
    }),
    expectedCTR: z.number().min(0, {
      message: formatMessage({
        defaultMessage: 'Expected CTR must be a valid value.',
        id: 'ioSettings.errors.pricingModelConfig.expectedCTR'
      })
    }),
    expectedSVTR: z.number().min(0, {
      message: formatMessage({
        defaultMessage: 'Expected SVR must be a valid value.',
        id: 'ioSettings.errors.pricingModelConfig.expectedSVTR'
      })
    }),
    expectedVTR: z.number().min(0, {
      message: formatMessage({
        defaultMessage: 'Expected VTR must be a valid value.',
        id: 'ioSettings.errors.pricingModelConfig.expectedVTR'
      })
    }),
    margin: z.record(
      z.number().min(0, {
        message: formatMessage({
          defaultMessage: 'The value must be valid.',
          id: 'ioSettings.errors.pricingModelConfig.numberValue'
        })
      })
    ),
    marginCPM: z.record(
      z.number().min(0, {
        message: formatMessage({
          defaultMessage: 'The value must be valid.',
          id: 'ioSettings.errors.pricingModelConfig.numberValue'
        })
      })
    ),
    markup: z.number().min(0, {
      message: formatMessage({
        defaultMessage: 'The value must be valid.',
        id: 'ioSettings.errors.pricingModelConfig.numberValue'
      })
    }),
    platformFee: z.number().min(0, {
      message: formatMessage({
        defaultMessage: 'The Platform Fee must be valid.',
        id: 'ioSettings.errors.pricingModelConfig.platformFee'
      })
    }),
    platformFeeModel: z.nativeEnum(PlatformFeeModelEnum, {
      message: formatMessage({
        defaultMessage: 'A Platform Fee Model must be chosen.',
        id: 'ioSettings.errors.pricingModelConfig.platformFeeModel'
      })
    })
  });
}

function calculateEffectiveDays(
  scheduleConfig: Record<string, number[]>,
  beginDate?: string | Date | null,
  endDate?: string | Date | null
): number {
  if (!beginDate || !endDate) {
    return 0;
  }

  const start = moment(beginDate);
  const end = moment(endDate);

  if (!start.isValid() || !end.isValid()) {
    return 0;
  }

  const dayNames = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  const effectiveDays = new Set<number>();

  const currentDate = start.clone();
  while (currentDate.isSameOrBefore(end)) {
    const dayOfWeek = dayNames[currentDate.day()];
    const hoursForDay = scheduleConfig[dayOfWeek];

    if (Array.isArray(hoursForDay) && hoursForDay.length > 0) {
      effectiveDays.add(currentDate.dayOfYear());
    }

    currentDate.add(1, 'day');
  }

  return effectiveDays.size;
}

function validatePpsvMinimumDays(
  data: any,
  ctx: z.RefinementCtx,
  formatMessage: IntlShape['formatMessage'],
  additionalProps: AdditionalValidatorProps
) {
  const pricingModel = additionalProps?.bidModels?.[0]?.bid?.pricingModel;
  if (pricingModel === PricingModelEnum.ppsv) {
    const scheduleConfig = data.config as Record<string, number[]>;
    const effectiveDays = calculateEffectiveDays(scheduleConfig, additionalProps.beginDate, additionalProps.endDate);

    if (effectiveDays < 10) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: formatMessage(
          {
            defaultMessage:
              'For PPSV pricing model, you must have at least 10 active days within the campaign date range. Current active days: {days}.',
            id: 'ioSettings.errors.scheduleParting.ppsvMinimumDays'
          },
          {
            days: String(effectiveDays)
          }
        )
      });
    }
  }
}

function validateWeekdayConsistency(
  data: any,
  ctx: z.RefinementCtx,
  formatMessage: IntlShape['formatMessage'],
  additionalProps: AdditionalValidatorProps
) {
  const c = data.config as any;
  const weekDays = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
  const pricingModel = additionalProps?.bidModels?.[0]?.bid?.pricingModel;
  if (pricingModel === PricingModelEnum.ppsv && weekDays.some(day => day in c)) {
    const configuredDays = weekDays.filter(day => Array.isArray(c[day]) && c[day].length > 0);

    if (configuredDays.length > 1) {
      const firstDayConfig = c[configuredDays[0]];
      const allSameConfig = configuredDays.every(day => {
        const dayConfig = c[day];
        return (
          Array.isArray(dayConfig) &&
          dayConfig.length === firstDayConfig.length &&
          dayConfig.every((hour: number, index: number) => hour === firstDayConfig[index])
        );
      });

      if (!allSameConfig) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: formatMessage({
            defaultMessage: 'All configured weekdays must have the same hour configuration.',
            id: 'ioSettings.errors.scheduleParting.inconsistentWeeklyConfig'
          })
        });
      }
    }
  }
}

function getSchedulePartingSchema(
  formatMessage: IntlShape['formatMessage'],
  additionalProps: AdditionalValidatorProps
) {
  const hoursArray = z.array(z.number().int().min(0).max(23));

  const weekConfigSchema = z
    .object({
      fri: hoursArray,
      mon: hoursArray,
      sat: hoursArray,
      sun: hoursArray,
      thu: hoursArray,
      tue: hoursArray,
      wed: hoursArray
    })
    .strict();

  const monthDayConfigSchema = z
    .object({
      day: hoursArray
    })
    .strict();

  const dateConfigSchema = z.record(z.string().regex(/^\d{4}-\d{2}-\d{2}$/), hoursArray);

  return z
    .object({
      config: z.union([weekConfigSchema, monthDayConfigSchema, dateConfigSchema]),
      enabled: z.boolean(),
      yearDate: z.object({
        aggregated: z.boolean(),
        enabled: z.boolean()
      })
    })
    .superRefine((data, ctx) => {
      if (additionalProps?.type) {
        const c = data.config as any;

        const ok = 'day' in c ? c.day?.length > 0 : Object.values(c).some(arr => (arr as number[])?.length > 0);

        if (!ok) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: formatMessage({
              defaultMessage: 'You need to configure at least one hour on any day of the week.',
              id: 'ioSettings.errors.scheduleAndDistribution'
            })
          });
        }
      }
    });
}

function getTargetingSchema(
  formatMessage: IntlShape['formatMessage'],
  options?: {
    allZonesRetargeting?: boolean;
    excludeAdExchanges?: boolean;
    hasPmpBidModel?: boolean;
    isDooh?: boolean;
  }
) {
  const baseSchema = z.object({
    adExchanges: z
      .array(
        z.object({
          blacklisted: z.boolean(),
          distribution: z.number(),
          name: z.nativeEnum(AdExchangeCodesEnum)
        })
      )
      .optional()
      .refine(
        adExchanges => {
          const distributionTotal = adExchanges?.reduce((sum, { distribution }) => sum + distribution, 0) ?? 0;
          return distributionTotal === 100 || distributionTotal === 0;
        },
        {
          message: formatMessage({
            defaultMessage: 'Ad Exchange distributions must total 100% or 0%.',
            id: 'ioSettings.errors.targeting.supplySource.wrongDistribution'
          })
        }
      )
      .refine(
        adExchanges => {
          return !adExchanges?.every(ad => ad.blacklisted);
        },
        {
          message: formatMessage({
            defaultMessage: 'Please, select at least one Ad Exchange.',
            id: 'ioSettings.errors.targeting.supplySource.noAdExchange'
          })
        }
      ),
    contextualTargeting: z
      .object({
        distribution: z.object({
          enabled: z.boolean().optional(),
          value: z.number().min(0).max(100)
        }),
        iabCategories: z.object({
          avoid: z.boolean().optional(),
          categories: z.array(z.string()),
          enabled: z.boolean().optional(),
          version: z.number()
        }),
        iasCategories: z.object({
          avoid: z.boolean().optional(),
          categories: z.array(z.string()),
          enabled: z.boolean().optional(),
          version: z.number()
        })
      })
      .refine(
        contextualTargeting => {
          if (contextualTargeting.iabCategories.enabled && !contextualTargeting.iabCategories.categories.length) {
            return false;
          }
          return true;
        },
        {
          message: formatMessage({
            defaultMessage: 'Please, select at least one IAB category',
            id: 'ioSettings.errors.targeting.contextualTargeting.noIABCategory'
          })
        }
      )
      .refine(
        contextualTargeting => {
          if (contextualTargeting.iasCategories.enabled && !contextualTargeting.iasCategories.categories.length) {
            return false;
          }
          return true;
        },
        {
          message: formatMessage({
            defaultMessage: 'Please, select at least one IAS category',
            id: 'ioSettings.errors.targeting.contextualTargeting.noIASCategory'
          })
        }
      )
      .optional(),
    deals: z.array(z.unknown()).optional(),
    temperatureAndWeather: z.object({
      temperature: z
        .object({
          max: z.string(),
          min: z.string()
        })
        .optional()
        .refine(temperature => !(temperature?.min && !temperature?.max), {
          message: formatMessage({
            defaultMessage: 'Maximum temperature must be filled correctly',
            id: 'ioSettings.errors.targeting.temperatureAndWeather.noMax'
          })
        })
        .refine(temperature => !(temperature?.max && !temperature?.min), {
          message: formatMessage({
            defaultMessage: 'Minimum temperature must be filled correctly',
            id: 'ioSettings.errors.targeting.temperatureAndWeather.noMin'
          })
        })
        .refine(temperature => !(temperature?.max && temperature?.min && temperature.max < temperature.min), {
          message: formatMessage({
            defaultMessage: 'Maximum temperature has to be greater than the minimum temperature',
            id: 'ioSettings.errors.targeting.temperatureAndWeather.maxMin'
          })
        })
    })
  });

  const schema = options?.excludeAdExchanges ? baseSchema.omit({ adExchanges: true }) : baseSchema;

  const refinedSchema = schema.superRefine((data, ctx) => {
    if (options?.allZonesRetargeting) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: formatMessage({
          defaultMessage: 'At least one zone must be set to Targeting.',
          id: 'ioSettings.retargeting.allRetargetingError'
        }),
        path: ['retargeting']
      });
    }

    if ((options?.isDooh || options?.hasPmpBidModel) && !data.deals?.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: formatMessage({
          defaultMessage: 'Please, select at least one Deal.',
          id: 'ioSettings.errors.targeting.deals.noDeals'
        }),
        path: ['appAndSiteMarketplace', 'deals']
      });
    }
  });

  return refinedSchema.optional();
}

function getTimezoneSchema(formatMessage: IntlShape['formatMessage']) {
  return z
    .string()
    .trim()
    .min(1, {
      message: formatMessage({
        defaultMessage: 'It is necessary to select a time zone.',
        id: 'ioSettings.errors.timezone'
      })
    });
}

function getTypeSchema(formatMessage: IntlShape['formatMessage']) {
  return z.nativeEnum(CampaignFamilyEnum, {
    message: formatMessage({
      defaultMessage: 'Media Channel is required.',
      id: 'ioSettings.errors.mediaChannel'
    })
  });
}

function getUserFrequencyCappingSchema(formatMessage: IntlShape['formatMessage']) {
  return z
    .object({
      pacing: z.boolean(),
      userFrequencyCappingEnabled: z.boolean(),
      userFrequencyClickCappingEnabled: z.boolean(),
      userMaxClicks: z.preprocess(
        value => (typeof value === 'number' && !Number.isNaN(value) ? value : null),
        z.number().nullable().optional()
      ),
      userMaxClicksHours: z.preprocess(
        value => (typeof value === 'number' && !Number.isNaN(value) ? value : null),
        z.number().nullable().optional()
      ),
      userMaxImpressions: z.preprocess(
        value => (typeof value === 'number' && !Number.isNaN(value) ? value : null),
        z.number().nullable().optional()
      ),
      userMaxImpressionsHours: z.preprocess(
        value => (typeof value === 'number' && !Number.isNaN(value) ? value : null),
        z.number().nullable().optional()
      )
    })
    .superRefine((data, ctx) => {
      if (data.userFrequencyCappingEnabled) {
        if (data.userMaxImpressions == null || Number.isNaN(data.userMaxImpressions)) {
          ctx.addIssue({
            code: 'custom',
            message: formatMessage({
              defaultMessage: 'Please check required fields',
              id: 'ioSettings.errors.userFrequencyCapping'
            }),
            path: ['userMaxImpressions']
          });
        }

        if (data.userMaxImpressionsHours == null || Number.isNaN(data.userMaxImpressionsHours)) {
          ctx.addIssue({
            code: 'custom',
            message: formatMessage({
              defaultMessage: 'Please check required fields',
              id: 'ioSettings.errors.userFrequencyCapping'
            }),
            path: ['userMaxImpressionsHours']
          });
        }
      }

      if (data.userFrequencyClickCappingEnabled) {
        if (data.userMaxClicks == null || Number.isNaN(data.userMaxClicks)) {
          ctx.addIssue({
            code: 'custom',
            message: formatMessage({
              defaultMessage: 'Please check required fields',
              id: 'ioSettings.errors.userFrequencyCapping'
            }),
            path: ['userMaxClicks']
          });
        }

        if (data.userMaxClicksHours == null || Number.isNaN(data.userMaxClicksHours)) {
          ctx.addIssue({
            code: 'custom',
            message: formatMessage({
              defaultMessage: 'Please check required fields',
              id: 'ioSettings.errors.userFrequencyCapping'
            }),
            path: ['userMaxClicksHours']
          });
        }
      }
    });
}

function validateDistribution(
  ads: any,
  ctx: z.RefinementCtx,
  formatMessage: IntlShape['formatMessage'],
  additionalProps: AdditionalValidatorProps
) {
  if (!Array.isArray(ads) || ads.length === 0) {
    return;
  }

  ads.forEach((ad, index) => {
    if (typeof ad.distribution !== 'number' || ad.distribution < 0 || ad.distribution > 100) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: formatMessage({
          defaultMessage: 'Ad distribution must be a number between 0 and 100',
          id: 'ioSettings.errors.adDistributionRange'
        }),
        path: [index, 'distribution']
      });
    }
  });

  if (additionalProps.isDistributionActive === false) {
    return;
  }

  const totalDistribution = ads.reduce(
    (sum, ad) => sum + (typeof ad.distribution === 'number' ? ad.distribution : 0),
    0
  );
  if (totalDistribution < 100 || totalDistribution > 100) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: formatMessage({
        defaultMessage: 'The sum of all ad distributions must be exactly 100',
        id: 'ioSettings.errors.distribution'
      })
    });
  }
}

function getAdsSchema(formatMessage: IntlShape['formatMessage'], additionalProps: AdditionalValidatorProps) {
  if (additionalProps.excludeAds) {
    return z.array(z.unknown()).optional();
  }
  return z.array(z.unknown()).min(1, {
    message: formatMessage({
      defaultMessage: 'You must configure at least one valid ad creative to be able to publish or update.',
      id: 'ioSettings.errors.adCreatives'
    })
  });
}

// Drafts keep the rest of the pricing section optional, but a price below the minimum is an invalid value
function getDraftPricingModelConfigSchema(
  formatMessage: IntlShape['formatMessage'],
  additionalProps: AdditionalValidatorProps
) {
  return z.object({ bidModels: getBidModelsSchema(formatMessage, additionalProps) }).optional();
}

function buildInsertionOrderObjectSchema(
  formatMessage: IntlShape['formatMessage'],
  additionalProps: AdditionalValidatorProps
) {
  return z.object({
    ads: getAdsSchema(formatMessage, additionalProps),
    countries: getCountriesSchema(formatMessage),
    dco: getDcoSchema(formatMessage),
    flights: getFlightsSchema(formatMessage, additionalProps),
    ioNumber: getIoNumberSchema(formatMessage),
    isPendingToCreate: z.boolean(),
    name: getNameSchema(formatMessage),
    pricingModelConfig: getPricingModelConfigSchema(formatMessage, additionalProps),
    scheduleParting: getSchedulePartingSchema(formatMessage, additionalProps),
    targeting: getTargetingSchema(formatMessage, {
      allZonesRetargeting: additionalProps.allZonesRetargeting,
      excludeAdExchanges: !!additionalProps.excludeAdExchanges
    }),
    type: getTypeSchema(formatMessage),
    userFrequencyCapping: getUserFrequencyCappingSchema(formatMessage)
  });
}

export function getInsertionOrderSchema(
  formatMessage: IntlShape['formatMessage'],
  additionalProps: AdditionalValidatorProps
) {
  return buildInsertionOrderObjectSchema(formatMessage, additionalProps);
}

export function getInsertionOrderDraftSchema(
  formatMessage: IntlShape['formatMessage'],
  additionalProps: AdditionalValidatorProps
) {
  return buildInsertionOrderObjectSchema(formatMessage, additionalProps)
    .pick({
      flights: true,
      ioNumber: true,
      name: true
    })
    .extend({ pricingModelConfig: getDraftPricingModelConfigSchema(formatMessage, additionalProps) });
}

function buildLineItemObjectSchema(
  formatMessage: IntlShape['formatMessage'],
  baseAdditionalProps: AdditionalValidatorProps
) {
  const additionalProps = { ...baseAdditionalProps, entityType: 'lineItem' as MultiFlightEntityType };
  return z.object({
    ads: getAdsSchema(formatMessage, additionalProps).superRefine((ads, ctx) => {
      validateDistribution(ads, ctx, formatMessage, additionalProps);
    }),
    beginDate: getBeginDateSchema(formatMessage),
    countries: getCountriesSchema(formatMessage),
    dco: getDcoSchema(formatMessage),
    endDate: getEndDateSchema(formatMessage),
    goals: getGoalsSchema(formatMessage),
    name: getNameSchema(formatMessage),
    pricingModelConfig: getPricingModelConfigSchema(formatMessage, additionalProps),
    scheduleParting: getSchedulePartingSchema(formatMessage, additionalProps).superRefine((data, ctx) => {
      validatePpsvMinimumDays(data, ctx, formatMessage, additionalProps);
      validateWeekdayConsistency(data, ctx, formatMessage, additionalProps);
    }),
    targeting: getTargetingSchema(formatMessage, {
      allZonesRetargeting: additionalProps.allZonesRetargeting,
      hasPmpBidModel: additionalProps.bidModels?.some(bidModel => bidModel.mediaType === BidModelsMediaTypeEnum.pmp),
      isDooh: additionalProps.type === CampaignFamilyEnum.dooh
    }),
    timezone: getTimezoneSchema(formatMessage),
    type: getTypeSchema(formatMessage),
    userFrequencyCapping: getUserFrequencyCappingSchema(formatMessage)
  });
}

export function getLineItemSchema(
  formatMessage: IntlShape['formatMessage'],
  additionalProps: AdditionalValidatorProps
) {
  return buildLineItemObjectSchema(formatMessage, additionalProps);
}

export function getLineItemDraftSchema(
  formatMessage: IntlShape['formatMessage'],
  additionalProps: AdditionalValidatorProps
) {
  return buildLineItemObjectSchema(formatMessage, additionalProps)
    .pick({ name: true })
    .extend({ pricingModelConfig: getDraftPricingModelConfigSchema(formatMessage, additionalProps) });
}
